











  import { Component, Vue } from 'nuxt-property-decorator'
  import { Action, namespace } from 'vuex-class'
  import { Account } from '@one/types'

  const account = namespace('account')

  /**
   * Routes whose content belongs to the client the user is leaving: a single order, invoice,
   * note, complaint or request for quotation is addressed by a number issued to that client,
   * and the cart, the checkout and a payment are that client's too. Those cannot survive the
   * switch, so they still land on the home page. Anything else - a product, a listing, an
   * account section - means the same thing for the client being entered, so the user stays
   * where they were.
   */
  const CLIENT_BOUND_ROUTES = [
    'orders-online',
    'orders-offline',
    'invoices-invoice',
    'goods-issued-notes-goods-issued-note',
    'goods-issued-notes-goods-received-note',
    'complaint',
    'rfq',
    'cart',
  ]
  const CLIENT_BOUND_ROUTE_PREFIXES = ['checkout', 'payment']

  @Component({
    components: {
      OneTopOrganizations: () => import('~/components/molecules/navigations/OneTopOrganizations.vue'),
    },
  })
  export default class OneOrganizationSwitcherComponent extends Vue {
    @account.Getter getClient: any
    @account.Getter('getSwitchableClients') switchableClients!: Array<Account.App.Responses.SwitchableClient>
    @account.Getter hasSwitchableClients!: boolean
    @Action switchOrganization: any

    switching: boolean = false

    get currentOrganizationName(): string | null {
      return (this.getClient && this.getClient.name) || null
    }

    get isOnClientBoundRoute(): boolean {
      const name = this.$route.name || ''
      return CLIENT_BOUND_ROUTES.includes(name) ||
        CLIENT_BOUND_ROUTE_PREFIXES.some(prefix => name === prefix || name.startsWith(`${prefix}-`))
    }

    async onOrganizationSelected(clientId: string) {
      if (this.switching) { return }
      this.switching = true
      try {
        await this.switchOrganization(clientId)
      } catch (e) {
        this.$logger.warn(e)
        this.$notify({
          group: 'alert',
          type: 'error',
          text: this.$t('header.switch_organization_error') as string,
        })
        return
      } finally {
        this.switching = false
      }

      if (this.isOnClientBoundRoute) {
        if (this.$route.path !== '/') {
          this.$router.push('/')
        }
        return
      }

      // The switch refetches what the store holds, but the page fetched its own data for the
      // client the user has just left. Both halves are needed: `refresh()` re-runs `asyncData`
      // and the route-level `fetch(context)`, the remount re-runs `created()`, which is where
      // most of the account section asks for its data.
      try {
        await this.$nuxt.refresh()
      } catch (e) {
        this.$logger.warn(e)
      }
      this.$store.commit('ORGANIZATION_SWITCHED')
    }
  }
